import cx from 'classnames'
import { useTranslation } from 'next-i18next'

/**
 * The excluded / included pill that adds taxes, insurance and HOA to a monthly
 * payment. Styled to match the calculator's switch (PDE-88).
 */
const PaymentExtrasSwitch = ({
  checked,
  onCheckedChange,
  labelledBy,
}: {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  /** Id of the visible label that names the switch. */
  labelledBy: string
}) => {
  const { t } = useTranslation('homes')

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={labelledBy}
      onClick={() => onCheckedChange(!checked)}
      className={cx(
        'relative h-[28px] w-[96px] shrink-0 rounded-full p-0 transition-colors duration-[180ms] ease-[ease] focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-3 focus-visible:ring-offset-2 motion-reduce:transition-none lg:h-24',
        // 44px tap target below lg without changing the visual height.
        "before:absolute before:-inset-y-8 before:inset-x-0 before:content-[''] lg:before:content-none",
        checked ? 'bg-teal-4' : 'bg-grey-4'
      )}
    >
      <span
        className={cx(
          'absolute left-2 top-2 h-24 w-24 rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.25)] transition-transform duration-[180ms] ease-[ease] motion-reduce:transition-none lg:h-20 lg:w-20',
          checked ? 'translate-x-[68px] lg:translate-x-[72px]' : 'translate-x-0'
        )}
      />
      {/* Both words stay rendered so the swap is a pure CSS crossfade: the
          outgoing word fades out, then the incoming one fades in after it.
          Each slot spans the space opposite the knob (63px) for headroom in
          any fallback font. */}
      {[
        {
          word: t('paymentExtras.excluded'),
          visible: !checked,
          slot: 'left-[29px] text-grey-8 lg:left-[27px]',
        },
        {
          word: t('paymentExtras.included'),
          visible: checked,
          slot: 'left-[4px] text-white lg:left-[6px]',
        },
      ].map(({ word, visible, slot }) => (
        <span
          key={slot}
          aria-hidden={visible ? undefined : 'true'}
          className={cx(
            'absolute inset-y-0 flex w-[63px] items-center justify-center overflow-hidden whitespace-nowrap text-12 font-semibold leading-none transition-opacity duration-[90ms] motion-reduce:transition-none',
            slot,
            visible ? 'opacity-100 delay-[90ms]' : 'opacity-0 delay-0'
          )}
        >
          {word}
        </span>
      ))}
    </button>
  )
}

export default PaymentExtrasSwitch
